import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Title } from '@/components/Elements/Title/Title';
import styles from './set-new-password.module.scss';
import {
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { useUpdateIdentityPasswordMutation } from '@/generated/graphql';
import { SetNewPasswordForm } from '@/components/Views/SetNewPassword/SetNewPasswordForm/SetNewPasswordForm';
import { HaveAnAccountLink } from '@/components/Elements/HaveAnAccountLink/HaveAnAccountLink';
import { PageLoader } from '@/components/Elements/PageLoader/PageLoader';
import { useSearchParams } from 'react-router-dom';
import { parseRecoveryToken } from '@/utils/parseRecoveryToken';
import { routes } from '@/constants/routes';
import { Error } from '@/enums/Error';
import { LoginView } from '@/enums/LoginView';
import { useMounted } from '@/hooks/useMounted';
import { PasswordNotification } from '@/components/Elements/PasswordNotif/PasswordNotification';
import { useRedirectAppRouter } from '@/hooks/useRedirectAppRouter';
import { useAuthBranding } from '@/components/Providers/BrandingProvider/BrandingProvider';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const SetNewPasswordPage = () => {
  const router = useRedirectAppRouter();
  const { t } = useTranslation();
  const { handleMsgType } = useNotification();
  const { branding } = useAuthBranding();
  useDocumentTitle(
    branding ? t('setNewPassword.title') : t('setNewPassword.pageTitle')
  );

  const [submitting, setSubmitting] = useState(false);

  const [searchParams] = useSearchParams();
  const parsedRecoveryToken = parseRecoveryToken(searchParams);

  const handleResetPasswordSuccess = () => {
    handleMsgType({
      type: TNotification.success,
      title: t('setNewPassword.passwordChanged'),
      message: t('setNewPassword.pleaseSignIn'),
    });
    router.pushWithRedirectAppParam(routes.login, {
      view: LoginView.EMAIL_PASSWORD,
    });
  };

  const [resetPassword] = useUpdateIdentityPasswordMutation({
    onCompleted: () => {
      handleResetPasswordSuccess();
    },
    onError: ({ graphQLErrors }) => {
      setSubmitting(false);
      graphQLErrors.forEach(({ message, extensions }) => {
        if (message === Error.RECOVERY_TOKEN_EXPIRED_OR_INVALID) {
          handleMsgType({
            type: TNotification.error,
            title: t('setNewPassword.expiredRecoveryLink'),
            message: t('setNewPassword.recoveryLinkIsInvalid'),
          });
        } else if (message === Error.PASSWORD_PREVIOUSLY_USED) {
          handleMsgType({
            type: TNotification.error,
            title: t('setNewPassword.weCouldNotResetPassword'),
            message: t('setNewPassword.passwordPreviouslyUsed'),
          });
        } else {
          handleMsgType({
            type: TNotification.error,
            title: t('setNewPassword.weCouldNotResetPassword'),
            message: t('setNewPassword.pleaseTryAgainOrContactSupport'),
          });
        }
      });
    },
  });

  const mounted = useMounted();

  useEffect(() => {
    if (!parsedRecoveryToken) {
      router.pushWithRedirectAppParam(routes.login);
    }
  }, []);

  const handlePasswordReset = (password: string) => {
    if (parsedRecoveryToken?.token) {
      setSubmitting(true);
      resetPassword({
        variables: {
          recoveryToken: parsedRecoveryToken.token,
          newPassword: password,
        },
      });
    }
  };

  return (
    <section>
      {!mounted ? (
        <PageLoader />
      ) : (
        <>
          <Title title={t('setNewPassword.title')} className={styles.title} />
          <PasswordNotification />
          <SetNewPasswordForm
            submitting={submitting}
            handlePasswordReset={handlePasswordReset}
          />
          <HaveAnAccountLink />
        </>
      )}
    </section>
  );
};

export default SetNewPasswordPage;
